import { useTranslation } from "react-i18next";

import { DATE_FORMATS_FNS } from "components";
import { ROBList } from "components/ROB/components";
import "components/ROB/components/RobSection/ROBSection.scss";
import { getTranslatedLabel } from "utils/common";
import { formatDate, isDate } from "utils/dateHelperFunctions";

import type { TFunction } from "i18next";

type RobSectionProps = {
  title: string;
  data: Record<string, unknown>;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const getValueForDisplay = (value: unknown, t: TFunction): string => {
  if (typeof value === "string") {
    if (!value) return "";
    if (isDate(value)) return formatDate(value, DATE_FORMATS_FNS.dateMonthYear);

    return getTranslatedLabel(value, t);
  }

  if (typeof value === "boolean") return value ? t("v8_yes", "Yes") : t("v8_no", "No");

  return String(value);
};

const renderSection = (data: Record<string, unknown>, t: TFunction, title?: string, subSection?: boolean) => {
  const rows = Object.entries(data).map(([key, value]): JSX.Element | null => {
    if (value === null) return null;
    if (Array.isArray(value)) return <ROBList key={key} title={key} list={value} onRenderEntry={entry => renderSection(entry, t)} />;
    if (isRecord(value)) return renderSection(value, t, key, true);

    return (
      <div className="section_row" key={key}>
        <div className="row_title">{getTranslatedLabel(key, t)}</div>
        <div className="row_value">{getValueForDisplay(value, t)}</div>
      </div>
    );
  });

  if (rows.every(row => !row)) return null;
  if (!title) return <>{rows}</>;

  return (
    <div className={subSection ? "sub_section" : "main_section"} key={title}>
      <div className="section_title">{getTranslatedLabel(title, t)}</div>
      {rows}
    </div>
  );
};

export const ROBSection = ({ title, data }: RobSectionProps) => {
  const { t } = useTranslation();

  if (!Object.keys(data).length) return null;

  return <div className="RobSection">{renderSection(data, t, title)}</div>;
};
